// Pole: a tube (a smooth centreline with a radius) fitted to the noisy points of one part, for legs,
// rails, ropes. The points are ordered along the part by geodesic distance over a voxel graph (so a
// bent rope works like a straight leg), circles fitted across slices give a first centreline that a
// one-sided scan doesn't pull towards the scanner, and Gauss-Newton on |p - C(t)| - r with Huber
// weights refines it, the radius alternating with it. A radius under the noise can't be measured:
// then it fits the centreline alone (unless the radius is given).
import { Band, solveSPD, mulBand } from './linalg.ts';
import { basis, curve, smoothFit1, value, penalty1, lambda } from './bspline.ts';
import { sigmaMAD, huberW, quantile } from './robust.ts';
import { solve } from '../align.ts';
import { eigSym3 } from '../edit/select.ts';
import type { PolePiece } from './doc.ts';

export interface PoleOpts {
  /** Control points (0: about one per 10 cm). */
  nc?: number;
  /** Smoothness (dimensionless; 1 by default). */
  s?: number;
  /** Radius (m; 0: fit it). */
  r?: number;
  /** Let the radius vary along the pole. */
  taper?: boolean;
  iters?: number;
}

export interface PoleFit extends PolePiece {
  len: number;
  mode: 'tube' | 'line';
  inliers: number;
  warn: string[];
}

type V = [number, number, number];
const sub = (a: ArrayLike<number>, b: ArrayLike<number>): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: ArrayLike<number>, b: ArrayLike<number>) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len3 = (a: ArrayLike<number>) => Math.sqrt(dot(a, a));
const unit = (a: V): V => { const l = len3(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
const cross = (a: ArrayLike<number>, b: ArrayLike<number>): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** Centroid and principal axes (columns of vecs, by decreasing variance). */
export function pca(xyz: ArrayLike<number>, idx?: ArrayLike<number>) {
  const n = idx ? idx.length : xyz.length / 3, c: V = [0, 0, 0];
  const at = (i: number) => (idx ? idx[i] : i);
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) c[k] += xyz[3 * at(i) + k] / n;
  const C = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < n; i++) {
    const d = sub([xyz[3 * at(i)], xyz[3 * at(i) + 1], xyz[3 * at(i) + 2]], c);
    for (let r = 0; r < 3; r++) for (let s = 0; s < 3; s++) C[3 * r + s] += (d[r] * d[s]) / n;
  }
  const { vals, vecs } = eigSym3(C), ord = [0, 1, 2].sort((a, b) => vals[b] - vals[a]);
  return { c, vals: ord.map(j => vals[j]), axes: ord.map(j => [vecs[j], vecs[3 + j], vecs[6 + j]] as V) };
}

/**
 * Where each point lies along the part, 0..1, by geodesic distance over a voxel graph from one end
 * (the farthest voxel from the farthest voxel); NaN for points the graph doesn't reach. len: the
 * geodesic length.
 */
export function orderAlong(xyz: Float32Array, vs: number): { t: Float64Array; len: number } {
  const n = xyz.length / 3, key = (x: number, y: number, z: number) => ((x + 32768) * 65536 + (y + 32768)) * 65536 + (z + 32768);
  const cells = new Map<number, number>(), cellOf = new Int32Array(n), cen: number[] = [], cnt: number[] = [], ijk: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = Math.floor(xyz[3 * i] / vs), y = Math.floor(xyz[3 * i + 1] / vs), z = Math.floor(xyz[3 * i + 2] / vs), k = key(x, y, z);
    let c = cells.get(k);
    if (c === undefined) { c = cells.size; cells.set(k, c); cen.push(0, 0, 0); cnt.push(0); ijk.push(x, y, z); }
    cellOf[i] = c;
    for (let d = 0; d < 3; d++) cen[3 * c + d] += xyz[3 * i + d];
    cnt[c]++;
  }
  const m = cells.size;
  for (let c = 0; c < m; c++) for (let d = 0; d < 3; d++) cen[3 * c + d] /= cnt[c];
  const nb: number[][] = Array.from({ length: m }, () => []);
  for (let c = 0; c < m; c++) for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
    const d = cells.get(key(ijk[3 * c] + i, ijk[3 * c + 1] + j, ijk[3 * c + 2] + k));
    if (d !== undefined && d !== c) nb[c].push(d);
  }
  const dijkstra = (src: number) => {
    const dist = new Float64Array(m).fill(Infinity), heap: [number, number][] = [];
    const push = (d: number, c: number) => {
      heap.push([d, c]);
      for (let i = heap.length - 1; i > 0;) { const p = (i - 1) >> 1; if (heap[p][0] <= heap[i][0]) break; [heap[p], heap[i]] = [heap[i], heap[p]]; i = p; }
    };
    const pop = () => {
      const top = heap[0], last = heap.pop()!;
      if (heap.length) {
        heap[0] = last;
        for (let i = 0; ;) {
          const l = 2 * i + 1, r = l + 1;
          let s = i;
          if (l < heap.length && heap[l][0] < heap[s][0]) s = l;
          if (r < heap.length && heap[r][0] < heap[s][0]) s = r;
          if (s === i) break;
          [heap[s], heap[i]] = [heap[i], heap[s]]; i = s;
        }
      }
      return top;
    };
    dist[src] = 0; push(0, src);
    while (heap.length) {
      const [d, c] = pop();
      if (d > dist[c]) continue;
      for (const e of nb[c]) {
        const w = d + Math.hypot(cen[3 * e] - cen[3 * c], cen[3 * e + 1] - cen[3 * c + 1], cen[3 * e + 2] - cen[3 * c + 2]);
        if (w < dist[e]) { dist[e] = w; push(w, e); }
      }
    }
    return dist;
  };
  const far = (d: Float64Array) => { let b = 0; for (let c = 0; c < m; c++) if (d[c] < Infinity && d[c] > d[b]) b = c; return b; };
  let start = 0;
  for (let c = 1; c < m; c++) if (cnt[c] > cnt[start]) start = c;
  const a = far(dijkstra(start)), dA = dijkstra(a), L = dA[far(dA)];
  const t = new Float64Array(n);
  for (let i = 0; i < n; i++) t[i] = L > 0 && dA[cellOf[i]] < Infinity ? dA[cellOf[i]] / L : NaN;
  return { t, len: L };
}

/**
 * Circle through 2-D points (x, y pairs): Kåsa's algebraic fit, then Gauss-Newton on the distances
 * with Huber weights. With r given, only the centre is fitted. null if it doesn't come out.
 */
export function fitCircle(q: ArrayLike<number>, rFixed = 0): { cx: number; cy: number; r: number; res: number[] } | null {
  const m = q.length / 2;
  if (m < (rFixed ? 3 : 5)) return null;
  let cx = 0, cy = 0, r = rFixed;
  for (let i = 0; i < m; i++) { cx += q[2 * i] / m; cy += q[2 * i + 1] / m; }
  let ext = 0;
  for (let i = 0; i < m; i++) ext = Math.max(ext, Math.hypot(q[2 * i] - cx, q[2 * i + 1] - cy));
  if (!rFixed) {
    const H = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], g = [0, 0, 0];
    for (let i = 0; i < m; i++) {
      const x = q[2 * i] - cx, y = q[2 * i + 1] - cy, row = [x, y, 1], z = -(x * x + y * y);
      for (let a = 0; a < 3; a++) { g[a] += row[a] * z; for (let b = 0; b < 3; b++) H[a][b] += row[a] * row[b]; }
    }
    const [D, E, F] = solve(H, g), ox = -D / 2, oy = -E / 2, r2 = ox * ox + oy * oy - F;
    if (!(r2 > 0)) return null;
    cx += ox; cy += oy; r = Math.sqrt(r2);
  }
  const res = new Array<number>(m), w = new Float64Array(m);
  for (let it = 0; it < 8; it++) {
    for (let i = 0; i < m; i++) res[i] = Math.hypot(q[2 * i] - cx, q[2 * i + 1] - cy) - r;
    huberW(res, sigmaMAD(res), 0, w);
    const np = rFixed ? 2 : 3, H = Array.from({ length: np }, () => new Array(np).fill(0)), g = new Array(np).fill(0);
    for (let i = 0; i < m; i++) {
      const dx = q[2 * i] - cx, dy = q[2 * i + 1] - cy, rho = Math.hypot(dx, dy) || 1e-12;
      const J = [-dx / rho, -dy / rho, -1];
      for (let a = 0; a < np; a++) { g[a] -= w[i] * J[a] * res[i]; for (let b = 0; b < np; b++) H[a][b] += w[i] * J[a] * J[b]; }
    }
    for (let a = 0; a < np; a++) H[a][a] *= 1 + 1e-6;
    const d = solve(H, g);
    if (!d.every(Number.isFinite)) return null;
    cx += d[0]; cy += d[1]; if (!rFixed) r += d[2];
    if (Math.hypot(...d) < 1e-7) break;
  }
  if (!(r > 0) || r > 4 * ext + 1e-3) return null;
  for (let i = 0; i < m; i++) res[i] = Math.hypot(q[2 * i] - cx, q[2 * i + 1] - cy) - r;
  return { cx, cy, r, res };
}

/** Two unit vectors across direction d. */
function across(d: V): [V, V] {
  const a: V = Math.abs(d[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0], e1 = unit(cross(d, a));
  return [e1, cross(d, e1)];
}

export function fitPole(xyz: Float32Array, o: PoleOpts = {}): PoleFit {
  const n = xyz.length / 3, warn: string[] = [], s = o.s ?? 1, rGiven = o.r && o.r > 0 ? o.r : 0;
  if (n < 20) throw new Error('too few points for a pole');
  const P = (i: number): V => [xyz[3 * i], xyz[3 * i + 1], xyz[3 * i + 2]];
  const pc = pca(xyz);
  // ── where each point is along it
  const ext = 2 * Math.sqrt(pc.vals[0]) * Math.sqrt(3);
  let { t, len } = orderAlong(xyz, Math.max(0.03, ext / 300));
  if (len < 0.1 || t.some(x => x !== x)) {
    // too short for the graph (or in pieces): along the main axis
    let lo = Infinity, hi = -Infinity;
    const pr = new Float64Array(n);
    for (let i = 0; i < n; i++) { pr[i] = dot(sub(P(i), pc.c), pc.axes[0]); lo = Math.min(lo, pr[i]); hi = Math.max(hi, pr[i]); }
    t = pr.map(x => (x - lo) / (hi - lo || 1));
    len = hi - lo;
  }
  if (pc.vals[0] < 4 * pc.vals[1]) warn.push('not pole-like');
  const nc = o.nc && o.nc >= 4 ? Math.min(64, Math.round(o.nc)) : Math.max(4, Math.min(32, Math.round(len / 0.1)));
  // ── slices across it: centres (circles) and the radius
  const ns = Math.max(4, Math.min(3 * nc, 96, Math.floor(n / 10)));
  const slices: number[][] = Array.from({ length: ns }, () => []);
  for (let i = 0; i < n; i++) slices[Math.min(ns - 1, Math.floor(t[i] * ns))].push(i);
  const cen = slices.map(ix => ix.length ? pca(xyz, ix).c : null);
  const centres: { t: number; p: V; c: V; w: number }[] = [], radii: number[] = [], circRes: number[] = [];
  for (let k = 0; k < ns; k++) {
    const ix = slices[k], c = cen[k];
    if (!c) continue;
    let lo = k - 1, hi = k + 1;
    while (lo >= 0 && !cen[lo]) lo--;
    while (hi < ns && !cen[hi]) hi++;
    let d = sub(cen[Math.min(hi, ns - 1)] ?? c, cen[Math.max(lo, 0)] ?? c);
    if (len3(d) < 1e-6) d = pc.axes[0];
    const dir = unit(d), [e1, e2] = across(dir), q = new Float64Array(2 * ix.length);
    let tm = 0;
    ix.forEach((i, j) => { const v = sub(P(i), c); q[2 * j] = dot(v, e1); q[2 * j + 1] = dot(v, e2); tm += t[i] / ix.length; });
    const f = ix.length >= 8 ? fitCircle(q, rGiven) : null;
    const p: V = f ? [c[0] + f.cx * e1[0] + f.cy * e2[0], c[1] + f.cx * e1[1] + f.cy * e2[1], c[2] + f.cx * e1[2] + f.cy * e2[2]] : c;
    if (f) { radii.push(f.r); circRes.push(...f.res); }
    centres.push({ t: tm, p, c, w: ix.length });
  }
  const r0 = radii.length ? quantile(radii, 0.5) : 0, sig0 = sigmaMAD(circRes);
  const mode: 'tube' | 'line' = rGiven || (radii.length >= ns / 2 && r0 > 1.5 * sig0) ? 'tube' : 'line';
  const fitXYZ = (ts: ArrayLike<number>, pts: (k: number) => number, w: ArrayLike<number> | null, sm: number) => {
    const c = new Float64Array(3 * nc);
    for (let d = 0; d < 3; d++) {
      const y = new Float64Array(ts.length);
      for (let i = 0; i < ts.length; i++) y[i] = pts(3 * i + d);
      smoothFit1(ts, y, w, nc, sm).forEach((v, k) => { c[3 * k + d] = v; });
    }
    return c;
  };
  // centreline mode: the slice centroids, not circles
  let C = fitXYZ(centres.map(x => x.t), k => (mode === 'tube' ? centres[Math.floor(k / 3)].p : centres[Math.floor(k / 3)].c)[k % 3], centres.map(x => x.w), s);
  const nr = mode === 'tube' && o.taper && !rGiven ? 4 : 1;
  let R: Float64Array = Float64Array.from({ length: nr }, () => rGiven || r0);

  // ── refine
  const tt = new Float64Array(n), d = new Float64Array(n), e = new Float64Array(n), nh = new Float64Array(3 * n);
  let w = new Float64Array(n).fill(1), sigma = sig0 || 0.01;
  const foot = [0, 0, 0], der = [0, 0, 0];
  /** closest point on the curve for every point: nearest of the samples, then Newton */
  const project = () => {
    const M = 10 * nc + 1, S = new Float64Array(3 * M);
    for (let j = 0; j < M; j++) S.set(curve(C, nc, j / (M - 1), foot), 3 * j);
    for (let i = 0; i < n; i++) {
      const p = P(i);
      let best = 0, bd = Infinity;
      for (let j = 0; j < M; j++) { const dx = p[0] - S[3 * j], dy = p[1] - S[3 * j + 1], dz = p[2] - S[3 * j + 2], q = dx * dx + dy * dy + dz * dz; if (q < bd) { bd = q; best = j; } }
      let u = best / (M - 1);
      for (let it = 0; it < 4; it++) {
        curve(C, nc, u, foot, der);
        const dd = dot(der, der);
        if (!(dd > 0)) break;
        const step = dot(sub(p, foot), der) / dd;
        u = Math.min(1, Math.max(0, u + step));
        if (Math.abs(step) < 1e-7) break;
      }
      curve(C, nc, u, foot);
      tt[i] = u;
      const v = sub(p, foot), l = len3(v);
      d[i] = l;
      nh[3 * i] = l > 0 ? v[0] / l : 0; nh[3 * i + 1] = l > 0 ? v[1] / l : 0; nh[3 * i + 2] = l > 0 ? v[2] / l : 0;
    }
  };
  const residuals = () => {
    for (let i = 0; i < n; i++) e[i] = mode === 'tube' ? d[i] - value(R, nr, tt[i]) : d[i];
  };
  const weigh = (trim: boolean) => {
    if (mode === 'tube') { sigma = sigmaMAD(e, null) || 1e-4; w = huberW(e, sigma, trim ? 3.5 : 0); }
    else {
      const med = quantile(d, 0.5) || 1e-4;
      sigma = med;
      for (let i = 0; i < n; i++) w[i] = trim && d[i] > 4 * med ? 0 : d[i] <= 2.5 * med ? 1 : (2.5 * med) / d[i];
    }
  };
  /**
   * Reparametrise by arc length, ending where the points end. Along the pole the points are uniform
   * blurred by the noise σ, so the true end is where that blur leaves σ / (√(2π) L) of them beyond it.
   */
  const reshape = () => {
    const M = 20 * nc, arcAt = new Float64Array(M + 1), S: V[] = [];
    for (let j = 0; j <= M; j++) {
      S.push(curve(C, nc, j / M, [0, 0, 0]) as V);
      if (j) arcAt[j] = arcAt[j - 1] + len3(sub(S[j], S[j - 1]));
    }
    const Ltot = arcAt[M] || 1e-9, ends = [0, 1].map(end => {
      const dv = [0, 0, 0];
      curve(C, nc, end, [0, 0, 0], dv);
      return { c: S[end * M], dir: unit([dv[0], dv[1], dv[2]]) };
    });
    const coord: number[] = [];
    for (let i = 0; i < n; i++) {
      if (!(w[i] > 0)) continue;
      const x = tt[i] * M, j = Math.min(M - 1, Math.floor(x));
      let c = arcAt[j] + (x - j) * (arcAt[j + 1] - arcAt[j]);
      if (tt[i] <= 0) c = Math.min(c, dot(sub(P(i), ends[0].c), ends[0].dir));
      if (tt[i] >= 1) c = Math.max(c, Ltot + dot(sub(P(i), ends[1].c), ends[1].dir));
      coord.push(c);
    }
    const sd = mode === 'tube' ? sigma : sigma / 1.18, f = Math.min(0.2, sd / (2.5066 * Math.max(Ltot, 1e-3)));
    const lo = quantile(coord, f), hi = quantile(coord, 1 - f);
    if (!(hi > lo)) return;
    const at = (c: number): { p: V; u: number } => {
      if (c <= 0) return { p: [ends[0].c[0] + c * ends[0].dir[0], ends[0].c[1] + c * ends[0].dir[1], ends[0].c[2] + c * ends[0].dir[2]], u: 0 };
      if (c >= Ltot) { const x = c - Ltot; return { p: [ends[1].c[0] + x * ends[1].dir[0], ends[1].c[1] + x * ends[1].dir[1], ends[1].c[2] + x * ends[1].dir[2]], u: 1 }; }
      let j = 0, k = M;
      while (k - j > 1) { const m = (j + k) >> 1; if (arcAt[m] <= c) j = m; else k = m; }
      const u = (j + (c - arcAt[j]) / (arcAt[k] - arcAt[j] || 1)) / M;
      return { p: curve(C, nc, u, [0, 0, 0]) as V, u };
    };
    const K = 12 * nc, us: number[] = [], pts: V[] = [], rs: number[] = [];
    for (let j = 0; j <= K; j++) { const q = at(lo + ((hi - lo) * j) / K); us.push(j / K); pts.push(q.p); rs.push(value(R, nr, q.u)); }
    C = fitXYZ(us, k => pts[Math.floor(k / 3)][k % 3], null, 0);
    if (nr > 1) R = smoothFit1(us, rs, null, nr, 0);
  };
  const Pen = penalty1(nc, 3, 0, undefined);
  penalty1(nc, 3, 1, Pen); penalty1(nc, 3, 2, Pen);
  const iters = o.iters ?? 8;
  for (let it = 0; it < iters; it++) {
    project(); residuals(); weigh(it >= 1);
    if (it < iters - 2) { reshape(); project(); residuals(); }
    if (mode === 'line') {
      C = fitXYZ(tt, k => xyz[k], w, s);
    } else {
      // Gauss-Newton on the centreline: ∂e_i/∂c_k = -B_k(t_i) n̂_i
      const A = new Band(3 * nc, 11), g = new Float64Array(3 * nc), N = new Float64Array(4);
      for (let i = 0; i < n; i++) {
        if (!w[i] || !d[i]) continue;
        const k0 = basis(tt[i], nc, N);
        for (let a = 0; a < 4; a++) for (let x = 0; x < 3; x++) {
          const ia = 3 * (k0 + a) + x, Ja = -N[a] * nh[3 * i + x];
          g[ia] += w[i] * Ja * e[i];
          for (let b = 0; b < 4; b++) for (let y = 0; y < 3; y++) {
            const ib = 3 * (k0 + b) + y;
            if (ib <= ia) A.add(ia, ib, w[i] * Ja * -N[b] * nh[3 * i + y]);
          }
        }
      }
      const lam = s > 0 ? lambda(s, A, Pen) : 0, mu = (1e-3 * A.trace()) / (3 * nc);
      const PC = mulBand(Pen, C);
      A.addScaled(Pen, lam);
      for (let i = 0; i < 3 * nc; i++) { A.a[i * (A.bw + 1)] += mu; g[i] += lam * PC[i]; }
      const step = solveSPD(A, g.map(x => -x));
      const cost = (c: Float64Array) => {
        let f = 0;
        for (let i = 0; i < n; i++) if (w[i]) { curve(c, nc, tt[i], foot); const r = len3(sub(P(i), foot)) - value(R, nr, tt[i]); f += w[i] * r * r; }
        const pc2 = mulBand(Pen, c);
        let q = 0;
        for (let k = 0; k < c.length; k++) q += c[k] * pc2[k];
        return f + lam * q;
      };
      const f0 = cost(C);
      for (let h = 1, tries = 0; tries < 5; tries++, h /= 2) {
        const C1 = C.map((x, k) => x + h * step[k]);
        if (cost(C1) <= f0) { C = C1; break; }
      }
    }
    if (mode === 'tube' && !rGiven) {
      project();
      const tw = new Float64Array(n);
      for (let i = 0; i < n; i++) tw[i] = w[i];
      R = smoothFit1(tt, d, tw, nr, nr > 1 ? 10 : 0);
      for (let k = 0; k < nr; k++) R[k] = Math.max(0, R[k]);
    }
  }
  project(); residuals(); weigh(true);
  let ss = 0, sw = 0, inl = 0;
  for (let i = 0; i < n; i++) if (w[i] > 0) { ss += e[i] * e[i]; sw++; inl++; }
  const rms = Math.sqrt(ss / Math.max(1, sw));
  if (mode === 'tube' && !rGiven) for (let k = 0; k < nr; k++) if (R[k] > 2 * rms) R[k] -= (rms * rms) / (2 * R[k]);
  if (mode === 'line') { R = Float64Array.of(0); warn.push('radius below the noise: set it'); }
  let L = 0;
  const prev = curve(C, nc, 0, [0, 0, 0]);
  for (let j = 1; j <= 100; j++) { const q = curve(C, nc, j / 100, [0, 0, 0]); L += len3(sub(q, prev)); prev[0] = q[0]; prev[1] = q[1]; prev[2] = q[2]; }
  if (mode === 'tube' && !rGiven && R.some(r => r < 2 * rms)) warn.push('radius near the noise: better set it');
  if (inl < 0.7 * n) warn.push('noisy: under 70% of the points fit');
  return { c: [...C], r: [...R], rms, n: inl, len: L, mode, inliers: inl / n, warn };
}
